import { useCallback, useEffect, useRef } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

/**
 * La mappa del report: una polilinea per tratta, colorata per stato (verde effettuata,
 * rosso non effettuata, grigio da verificare), popup al click e messa a fuoco della
 * tratta scelta dall'elenco.
 *
 * Leaflet e' usato direttamente (come in UploadReportHera): un'istanza per container,
 * creata una volta e aggiornata quando cambiano tratte o selezione. La mappa dentro un
 * modale o una colonna che cambia larghezza ha bisogno di invalidateSize(): lo si fa
 * dopo il mount e con un ResizeObserver sul container.
 *
 * Le tratte SENZA geometria non si disegnano: sono in elenco con l'avviso "non
 * georeferenziata". Se nessuna ha geometria, la mappa si centra su Bologna e lo dice.
 */

export const COLORE_STATO = {
    effettuata: "#16A34A",
    non_effettuata: "#DC2626",
    da_verificare: "#94A3B8",
    /* Solo dalla mappa di un percorso ancora aperto (dashboard): il report pubblico non lo usa.
       Grigio piu' scuro di da_verificare: un giro aperto e' TUTTO in attesa, e il grigio
       chiaro sparisce sulle strade grigie della mappa. */
    in_attesa: "#64748B",
    // Solo dalla mappa del percorso: non fatta nel giro, fatta dopo da un giro di recupero.
    recuperata: "#DC2626",
};
/* Stati disegnati tratteggiati: "recuperata" e' rossa come una non fatta (per il giro lo e'),
   il tratteggio dice che poi e' stata recuperata, come il bordo del badge dello Storico.
   Con lineCap round ogni trattino si allunga di mezzo spessore per lato: 8+12 resta leggibile. */
const TRATTEGGIO = { recuperata: "8 12" };
const CENTRO_DEFAULT = [44.4949, 11.3426]; // Bologna

/* L'arancione della tratta scelta: non e' uno dei colori di stato (verde/rosso/grigio),
   proprio perche' deve dire "questa" senza confondersi con "com'e' andata". Si disegna
   come alone sotto la linea, che resta del suo colore. */
const COLORE_SCELTA = "#F59E0B";

/* Raggruppamento delle foto: si lavora in PIXEL sullo schermo, non in gradi, cosi' il
   raggruppamento e' sempre "due dita di distanza" a qualunque zoom. Alzando lo zoom le
   foto vicine si separano da sole, come nelle mappe del telefono. */
const GRIGLIA_PX = 58;

/* `fotoVisibili`: mostra/nasconde le miniature SENZA cambiare `foto`. Le foto entrano nella
   firma dell'inquadratura: passarle vuote per nasconderle farebbe reinquadrare la mappa
   (e perdere lo zoom di chi la sta guardando).
   `popup`: false = niente popup sulle linee (ne' al clic ne' alla selezione), per chi mostra
   gia' il dettaglio della tratta accanto alla mappa (mappa del percorso nella dashboard). */
const MappaTratte = ({ tratte, mappa, selezionata, selezioneDaMappa = false, onSeleziona, foto = [], fotoVisibili = true, popup = true, onApriFoto, espansa = false, onEspandi, className = "" }) => {
    const nodeRef = useRef(null);
    const mapRef = useRef(null);
    const layersRef = useRef(new Map()); // codice -> polyline
    const groupRef = useRef(null);
    const fotoLayerRef = useRef(null);
    const fotoRef = useRef([]);          // foto correnti, lette dal ridisegno
    const fotoVisibiliRef = useRef(fotoVisibili);
    const onApriFotoRef = useRef(onApriFoto);
    useEffect(() => { onApriFotoRef.current = onApriFoto; }, [onApriFoto]);
    const sceltaLayerRef = useRef(null); // alone + copia della tratta scelta
    const firmaRef = useRef(null);       // cosa era inquadrato l'ultima volta
    const onSelezionaRef = useRef(onSeleziona);
    useEffect(() => { onSelezionaRef.current = onSeleziona; }, [onSeleziona]);

    // Istanza unica per container.
    useEffect(() => {
        const node = nodeRef.current;
        if (!node || mapRef.current) return undefined;
        const map = L.map(node, { scrollWheelZoom: false, tap: true }).setView(CENTRO_DEFAULT, 13);
        L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
            attribution: "&copy; OpenStreetMap contributors",
            maxZoom: 19,
        }).addTo(map);
        groupRef.current = L.featureGroup().addTo(map);
        // Click sulla mappa fuori da tutto: si torna a nessuna tratta scelta.
        map.on("click", () => { if (onSelezionaRef.current) onSelezionaRef.current(null); });
        /* Pane dedicato all'evidenziazione, sopra le altre linee: cosi' la tratta scelta
           non finisce sotto a una che le passa accanto. */
        map.createPane("sceltaPane");
        map.getPane("sceltaPane").style.zIndex = 620;
        sceltaLayerRef.current = L.layerGroup([], { pane: "sceltaPane" }).addTo(map);
        // Le foto stanno in un pane sopra le linee: sono il dettaglio che si guarda.
        map.createPane("fotoPane");
        map.getPane("fotoPane").style.zIndex = 650;
        fotoLayerRef.current = L.layerGroup([], { pane: "fotoPane" }).addTo(map);
        mapRef.current = map;
        // Lo scroll della pagina non deve diventare zoom per sbaglio; il click sulla
        // mappa lo riattiva, l'uscita col mouse lo spegne di nuovo.
        map.on("focus", () => map.scrollWheelZoom.enable());
        map.on("blur", () => map.scrollWheelZoom.disable());
        const t1 = setTimeout(() => map.invalidateSize(), 100);
        const t2 = setTimeout(() => map.invalidateSize(), 400);
        const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(() => map.invalidateSize()) : null;
        if (ro) ro.observe(node);
        return () => {
            clearTimeout(t1); clearTimeout(t2);
            if (ro) ro.disconnect();
            map.remove();
            mapRef.current = null;
            layersRef.current = new Map();
        };
    }, []);

    // Ridisegna le polilinee quando cambiano le tratte.
    useEffect(() => {
        const map = mapRef.current;
        const group = groupRef.current;
        if (!map || !group) return;
        group.clearLayers();
        layersRef.current = new Map();
        for (const t of tratte || []) {
            if (!t.geometria || !t.geometria.coordinates) continue;
            const colore = COLORE_STATO[t.stato] || COLORE_STATO.da_verificare;
            const linee = t.geometria.type === "MultiLineString" ? t.geometria.coordinates : [t.geometria.coordinates];
            // GeoJSON e' [lng, lat], Leaflet vuole [lat, lng].
            const latlngs = linee.map((l) => l.map(([lng, lat]) => [lat, lng]));
            const layer = L.polyline(latlngs, { color: colore, weight: 5, opacity: 0.85, lineCap: "round", dashArray: TRATTEGGIO[t.stato] || null });
            if (popup) layer.bindPopup(popupHtml(t), { maxWidth: 280 });
            layer.on("click", (e) => {
                L.DomEvent.stopPropagation(e);   // altrimenti scatta anche il click "a vuoto"
                // Toccando la tratta gia' scelta la si lascia andare: stesso gesto, verso opposto.
                if (onSeleziona) onSeleziona(t.codice, { daMappa: true, toggle: true });
            });
            layer.addTo(group);
            layersRef.current.set(t.codice, layer);
        }
        /* Il riquadro comprende linee e foto di CIO' CHE SI STA GUARDANDO: cambiando
           giornata l'inquadratura segue il giro di quel giorno. Ma si rifa' SOLO quando
           cambia davvero l'insieme mostrato: rifarlo a ogni ridisegno annullerebbe lo
           zoom sulla tratta appena scelta (e ogni spostamento fatto a mano).
           invalidateSize prima del fit, perche' il contenitore puo' aver cambiato
           misura (colonna, tutto schermo). */
        const firma = `${(tratte || []).map((t) => t.codice).join(",")}|${(foto || []).length}`;
        if (firma === firmaRef.current) return;
        firmaRef.current = firma;

        let bounds = group.getBounds();
        for (const f of foto || []) {
            if (f.lat == null || f.lng == null) continue;
            bounds = bounds.isValid() ? bounds.extend([f.lat, f.lng]) : L.latLngBounds([[f.lat, f.lng], [f.lat, f.lng]]);
        }
        map.invalidateSize();
        if (bounds.isValid()) {
            map.fitBounds(bounds.pad(0.08), { maxZoom: 16 });
        } else if (mappa && mappa.centro) {
            map.setView(mappa.centro, 14);
        } else {
            map.setView(CENTRO_DEFAULT, 13);
        }
    }, [tratte, foto, mappa, onSeleziona, popup]);

    /* Ridisegna i mucchietti di foto: si prendono quelle nel riquadro visibile, si
       raggruppano per cella di schermo e ogni gruppo diventa un marker con la miniatura
       (e il numero, se sono piu' d'una). Si rifa' a ogni movimento della mappa. */
    const disegnaFoto = useCallback(() => {
        const map = mapRef.current;
        const layer = fotoLayerRef.current;
        if (!map || !layer) return;
        layer.clearLayers();
        const elenco = fotoRef.current;
        if (!elenco.length || !fotoVisibiliRef.current) return;

        const bounds = map.getBounds().pad(0.2);
        const celle = new Map();
        for (const f of elenco) {
            if (f.lat == null || f.lng == null) continue;
            const ll = L.latLng(f.lat, f.lng);
            if (!bounds.contains(ll)) continue;
            const p = map.latLngToLayerPoint(ll);
            const chiave = `${Math.floor(p.x / GRIGLIA_PX)}|${Math.floor(p.y / GRIGLIA_PX)}`;
            if (!celle.has(chiave)) celle.set(chiave, []);
            celle.get(chiave).push(f);
        }

        for (const gruppo of celle.values()) {
            // Il marker si piazza sul baricentro del gruppo: cosi' non "salta" fra una
            // foto e l'altra quando il gruppo cambia composizione.
            const lat = gruppo.reduce((a, f) => a + f.lat, 0) / gruppo.length;
            const lng = gruppo.reduce((a, f) => a + f.lng, 0) / gruppo.length;
            const prima = gruppo[0];
            const n = gruppo.length;
            const html = `
              <div class="rp-fotopin ${n > 1 ? "multi" : ""}" style="--pin: ${COLORE_STATO[prima.stato] || COLORE_STATO.da_verificare}">
                ${n > 1 ? '<span class="rp-fotopin-dietro"></span>' : ""}
                <img src="${prima.thumb}" alt="" loading="lazy" onerror="this.closest('.rp-fotopin').classList.add('senza-img')" />
                ${n > 1 ? `<span class="rp-fotopin-n">${n}</span>` : ""}
              </div>`;
            const marker = L.marker([lat, lng], {
                pane: "fotoPane",
                icon: L.divIcon({ className: "rp-fotopin-wrap", html, iconSize: [46, 46], iconAnchor: [23, 23] }),
                keyboard: false,
                title: n > 1 ? `${n} foto` : prima.indirizzo || prima.tratta,
            });
            marker.on("click", () => {
                // Con tante foto ammucchiate conviene prima avvicinarsi, come fa il
                // telefono: solo quando sono poche si apre la galleria.
                if (n > 4 && map.getZoom() < map.getMaxZoom() - 2) {
                    map.setView([lat, lng], Math.min(map.getZoom() + 2, map.getMaxZoom()));
                    return;
                }
                if (onApriFotoRef.current) onApriFotoRef.current(gruppo);
            });
            marker.addTo(layer);
        }
    }, []);

    useEffect(() => {
        fotoRef.current = (foto || []).filter((f) => f.lat != null && f.lng != null);
        disegnaFoto();
    }, [foto, disegnaFoto]);

    // Accendere/spegnere le foto ridisegna solo le miniature: zoom e posizione restano.
    useEffect(() => {
        fotoVisibiliRef.current = fotoVisibili;
        disegnaFoto();
    }, [fotoVisibili, disegnaFoto]);

    useEffect(() => {
        const map = mapRef.current;
        if (!map) return undefined;
        map.on("moveend zoomend", disegnaFoto);
        return () => { map.off("moveend zoomend", disegnaFoto); };
    }, [disegnaFoto]);

    /* Evidenzia la tratta scelta. Se la scelta e' arrivata dall'elenco la mappa ci va
       sopra; se e' arrivata da un click sulla mappa no, perche' la si sta gia' guardando
       e uno zoom improvviso farebbe perdere il segno. */
    useEffect(() => {
        const map = mapRef.current;
        const scelta = sceltaLayerRef.current;
        if (!map || !scelta) return;
        scelta.clearLayers();
        if (!selezionata) { map.closePopup(); return; }

        const layer = layersRef.current.get(selezionata);
        if (!layer) return;
        const latlngs = layer.getLatLngs();
        const stile = layer.options.color;
        // Due linee sovrapposte: l'alone arancione che grida "questa", e sopra la tratta
        // col colore del suo stato, che resta leggibile.
        L.polyline(latlngs, { color: COLORE_SCELTA, weight: 13, opacity: 0.95, lineCap: "round", lineJoin: "round", pane: "sceltaPane", interactive: false }).addTo(scelta);
        const sopra = L.polyline(latlngs, { color: stile, weight: 5, opacity: 1, lineCap: "round", dashArray: layer.options.dashArray, pane: "sceltaPane" });
        if (layer.getPopup()) sopra.bindPopup(layer.getPopup());
        sopra.on("click", (e) => {
            L.DomEvent.stopPropagation(e);
            if (onSelezionaRef.current) onSelezionaRef.current(selezionata, { daMappa: true, toggle: true });
        });
        sopra.addTo(scelta);

        if (!selezioneDaMappa) {
            const b = layer.getBounds();
            // 17 e non 16: molte tratte ritagliate sono di un centinaio di metri e a
            // zoom piu' basso si perderebbero nel resto della mappa.
            if (b.isValid()) map.fitBounds(b.pad(0.5), { maxZoom: 17 });
        }
        if (popup) sopra.openPopup();
    }, [selezionata, selezioneDaMappa, tratte, popup]);

    /* Entrando e uscendo dal tutto schermo il contenitore cambia misura: Leaflet va
       avvisato, altrimenti restano i riquadri grigi delle tile non richieste. Il fit si
       rifa' per riempire lo spazio nuovo. */
    useEffect(() => {
        const map = mapRef.current;
        const group = groupRef.current;
        if (!map) return undefined;
        const aggiorna = () => {
            map.invalidateSize();
            const b = group && group.getBounds();
            if (b && b.isValid()) map.fitBounds(b.pad(0.08), { maxZoom: 16, animate: false });
        };
        const t = setTimeout(aggiorna, 120);
        return () => clearTimeout(t);
    }, [espansa]);

    const conGeometria = (tratte || []).filter((t) => t.geometria).length;

    return (
        <div className={`rp-mappa-wrap ${className}`.trim()}>
            <div ref={nodeRef} className="rp-mappa" role="region" aria-label="Mappa delle tratte" />
            {onEspandi && (
                <button type="button" className="rp-mappa-espandi" onClick={onEspandi}
                    aria-pressed={espansa}
                    title={espansa ? "Esci dal tutto schermo (Esc)" : "Mappa a tutto schermo"}>
                    {espansa ? (
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="4 14 10 14 10 20" /><polyline points="20 10 14 10 14 4" />
                            <line x1="14" y1="10" x2="21" y2="3" /><line x1="3" y1="21" x2="10" y2="14" />
                        </svg>
                    ) : (
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="15 3 21 3 21 9" /><polyline points="9 21 3 21 3 15" />
                            <line x1="21" y1="3" x2="14" y2="10" /><line x1="3" y1="21" x2="10" y2="14" />
                        </svg>
                    )}
                    <span>{espansa ? "Riduci" : "Tutto schermo"}</span>
                </button>
            )}
            {conGeometria === 0 && (
                <div className="rp-mappa-vuota">
                    Nessuna tratta di questo report è georeferenziata: consulta l’elenco qui sotto.
                </div>
            )}
        </div>
    );
};

const ETICHETTA_STATO = { effettuata: "Effettuata", non_effettuata: "Non effettuata", da_verificare: "Da verificare", in_attesa: "In attesa", recuperata: "Recuperata dopo" };
const esc = (s) => String(s ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));

/** Il popup della polilinea: via, stato, ultimo passaggio, foto. HTML a mano perche' Leaflet vuole una stringa. */
function popupHtml(t) {
    const colore = COLORE_STATO[t.stato] || COLORE_STATO.da_verificare;
    const fatti = (t.passaggi || []).filter((p) => p.esito === "fatta");
    const ultimo = fatti.length ? fatti[fatti.length - 1] : null;
    const quando = ultimo ? (ultimo.fattoIl ? formattaDataOra(ultimo.fattoIl) : formattaData(ultimo.data)) : null;
    return `
      <div class="rp-popup">
        <div class="rp-popup-via">${esc(t.strada)}</div>
        ${t.tratto ? `<div class="rp-popup-tratto">${esc(t.tratto)}</div>` : ""}
        <div class="rp-popup-riga"><span class="rp-dot" style="background:${colore}"></span>${ETICHETTA_STATO[t.stato] || t.stato}</div>
        ${quando ? `<div class="rp-popup-riga">Ultimo passaggio: ${esc(quando)}</div>` : ""}
        ${t.lunghezzaMetri ? `<div class="rp-popup-riga">${esc(t.lunghezzaMetri)} m</div>` : ""}
        ${(t.foto || []).length ? `<div class="rp-popup-riga">${t.foto.length} foto</div>` : ""}
        ${t.geometriaQualita === "via-intera" ? `<div class="rp-popup-nota">Tratto non delimitato: è evidenziata l’intera via</div>` : ""}
      </div>`;
}

export const formattaData = (iso) => (iso ? String(iso).slice(0, 10).split("-").reverse().join("/") : "—");
export const formattaDataOra = (iso) => {
    if (!iso) return "—";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return formattaData(iso);
    return d.toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
};

export default MappaTratte;
